import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { InvitationPreviewDTO, WorkshopAttendeeDTO, WorkshopDetailDTO } from "../../../../models/WorkshopDTO";
import S from "./strings";
import { BuscarAlumna, EstadoPill, Opciones, PromptModal, dinero, errorText } from "./comun";
import { WorkshopMultiDetailDTO } from "../../../../services/v2/workshops.service";
import { fechaHoraAmPm } from "../../../../utils/fechaHoraAmPm";
import { ChipsSesiones, Sesiones } from "./sesiones";
import { WorkshopSessionDTO } from "../../../../services/v2/workshops.service";
import WorkshopForm from "./workshopForm";
import { BotonWhatsappAlumno, useVeWappChat } from "../../../../components/whatsapp/whatsappAlumno";
import EnvioMasivo from "../../../../components/whatsapp/envioMasivo";

interface Props {
	campusId: number;
	id: number;
	opciones: Opciones;
	onBack: () => void;
	notify: (text: string, ok?: boolean) => void;
}


const DIAS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
/** "2026-10-25" -> "sábado 25 de octubre" (sin pasar por UTC). */
const fechaLarga = (ymd: string): string => {
	const [y, m, d] = ymd.substring(0, 10).split("-").map(Number);
	const f = new Date(y, m - 1, d);
	return `${DIAS[f.getDay()]} ${d} de ${MESES[m - 1]}`;
};
const horaAmPm = (hhmm: string): string => {
	const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ""));
	if (!m) return "";
	const h = Number(m[1]);
	return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? "am" : "pm"}`;
};
const unir = (xs: string[]): string => xs.length <= 1 ? (xs[0] || "") : xs.slice(0, -1).join(", ") + " y " + xs[xs.length - 1];
/**
 * Taller de varios días: {fecha} y {hora} del texto de la invitación se llenan con TODAS las sesiones
 * ("sábado 25 de octubre y sábado 1 de noviembre, de 9:00 am a 1:00 pm"). Con una sola sesión (o ninguna) se
 * deja el texto como está y lo llena el servidor con la fecha del taller.
 */
export const textoConSesiones = (texto: string, sesiones: WorkshopSessionDTO[] | undefined): string => {
	const ss = [...(sesiones || [])].sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime));
	if (ss.length < 2) return texto;
	const rango = (s: WorkshopSessionDTO): string => s.endTime ? `de ${horaAmPm(s.startTime)} a ${horaAmPm(s.endTime)}` : `a las ${horaAmPm(s.startTime)}`;
	const mismoHorario = ss.every(s => s.startTime === ss[0].startTime && (s.endTime || "") === (ss[0].endTime || ""));
	const fechas = unir(ss.map(s => fechaLarga(s.date)));
	const todo = mismoHorario ? `${fechas}, ${rango(ss[0])}` : unir(ss.map(s => `${fechaLarga(s.date)} ${rango(s)}`));
	const horas = mismoHorario ? rango(ss[0]).replace(/^(de|a las) /, "") : unir(ss.map(s => `${horaAmPm(s.startTime)}${s.endTime ? " a " + horaAmPm(s.endTime) : ""}`));
	return texto
		.replace(/\{fecha\}\s*(?:,\s*)?(?:a las|de las|a)\s*\{hora\}/gi, todo)
		.replace(/\{fecha\}/gi, fechas)
		.replace(/\{hora\}/gi, horas);
};

/** Invitación por WhatsApp: texto con {placeholders} y destinatarias por curso; el envío sale por WappChat (una a una o a varias). */
const Invitacion: React.FC<{ campusId: number; taller: WorkshopDetailDTO; sesiones?: WorkshopSessionDTO[]; opciones: Opciones; notify: Props["notify"] }> = ({ campusId, taller, sesiones, opciones, notify }) => {
	const http = HttpManager.getInstance();
	const [texto, setTexto] = useState<string>(taller.invitationText || "");
	const [cursos, setCursos] = useState<number[]>(taller.courseId ? [taller.courseId] : []);
	const [vista, setVista] = useState<InvitationPreviewDTO | null>(null);
	const [quitar, setQuitar] = useState<number[]>([]);
	const [busy, setBusy] = useState<boolean>(false);
	const [masivo, setMasivo] = useState<boolean>(false);
	const veWa = useVeWappChat();
	const area = React.useRef<HTMLTextAreaElement>(null);

	const ver = () => {
		if (cursos.length === 0) { notify(S.elegirCurso, false); return; }
		setBusy(true);
		//Taller de varios días: el servidor solo conoce la primera fecha, así que se le manda el texto ya con todas las sesiones.
		//Sin texto propio se pide primero la plantilla del servidor y se vuelve a pedir con las sesiones puestas.
		const pedir = (t: string) => http.workshopsService.invitationPreview(campusId, taller.id, cursos, t);
		pedir(textoConSesiones(texto, sesiones))
			.then(async r => {
				let res = r;
				if (!texto) { setTexto(r.data.template); if ((sesiones || []).length > 1) res = await pedir(textoConSesiones(r.data.template, sesiones)); }
				setVista(res.data);
				setQuitar(res.data.recipients.filter(x => x.alreadyEnrolled).map(x => x.studentId));
			})
			.catch(e => notify(errorText(e), false))
			.finally(() => setBusy(false));
	};
	const elegidas = (vista?.recipients || []).filter(r => !quitar.includes(r.studentId));
	const insertar = (ph: string) => {
		const el = area.current;
		const pos = el ? el.selectionStart : texto.length;
		setTexto(texto.slice(0, pos) + ph + texto.slice(pos));
	};

	return (
		<div className="wau-caja">
			<h3>{S.invitacion}</h3>
			<label className="wau-campo"><span className="wau-lab">{S.textoInvitacion}</span>
				<textarea ref={area} className="wau-entrada" rows={3} value={texto} onChange={e => setTexto(e.target.value)} /></label>
			<div className="wte-placeholders">
				{(vista?.placeholders || ["{nombre}", "{taller}", "{fecha}", "{hora}", "{plantel}", "{precio}"]).map(p => (
					<button key={p} type="button" className="wau-chip" style={{ padding: "2px 10px", fontSize: 13 }} onClick={() => insertar(p)}>{p}</button>
				))}
			</div>
			<span className="wau-lab">{S.cursosDestino}</span>
			<div className="wau-chips" role="group" style={{ marginTop: 4 }}>
				{opciones.cursos.map(c => (
					<button key={c.id} type="button" className="wau-chip" aria-pressed={cursos.includes(c.id)}
						onClick={() => { setVista(null); setCursos(cs => cs.includes(c.id) ? cs.filter(x => x !== c.id) : [...cs, c.id]); }}>{c.name}</button>
				))}
			</div>
			<div className="wau-acciones" style={{ justifyContent: "flex-start", marginTop: 0 }}>
				<button type="button" className="wau-btn s" disabled={busy || cursos.length === 0} onClick={ver}>{S.verDestinatarias}</button>
				{vista ? <span className="wau-sec">{S.destinatarias(vista.total)}</span> : null}
				{vista && veWa && elegidas.length > 0 ? <button type="button" className="wau-btn p" disabled={busy} onClick={() => setMasivo(true)}>{S.whatsappVarias(elegidas.length)}</button> : null}
			</div>
			{!vista ? null : (
				<div className="wau-tabla" style={{ marginTop: 10, maxHeight: 420, overflowY: "auto" }}>
					<table>
						<thead><tr><th></th><th>{S.colAlumna}</th><th>{S.curso}</th><th>{S.colTel}</th><th>{S.textoInvitacion}</th></tr></thead>
						<tbody>
							{vista.recipients.map(r => (
								<tr key={r.studentId + "_" + r.enrollmentId}>
									<td><input type="checkbox" checked={!quitar.includes(r.studentId)} aria-label={r.name}
										onChange={() => setQuitar(q => q.includes(r.studentId) ? q.filter(x => x !== r.studentId) : [...q, r.studentId])} /></td>
									<td><b>{r.name}</b>{r.alreadyEnrolled ? <div><span className="wau-pastilla acento">{S.yaInscrita}</span></div> : null}</td>
									<td style={{ fontSize: 13.5 }}>{r.courseName}</td>
									<td className="wau-num">{r.phone || "—"}</td>
									<td style={{ fontSize: 13, maxWidth: 380 }}>{r.text}
										<div><BotonWhatsappAlumno campusId={campusId} studentId={r.studentId} nombre={r.name} texto sugerido={r.text} /></div></td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			{masivo ? <EnvioMasivo campusId={campusId} studentIds={elegidas.map(r => r.studentId)} origen="taller" referencia={taller.name || null}
				onClose={() => setMasivo(false)} /> : null}
		</div>
	);
};

/** Detalle de un taller: datos, inscritas (agregar, asistió, baja) e invitación. */
const WorkshopDetail: React.FC<Props> = ({ campusId, id, opciones, onBack, notify }) => {
	const http = HttpManager.getInstance();
	const [t, setT] = useState<WorkshopMultiDetailDTO | null>(null);
	const [editar, setEditar] = useState<boolean>(false);
	const [cobra, setCobra] = useState<boolean>(true);
	const [precio, setPrecio] = useState<string>("");
	const [baja, setBaja] = useState<WorkshopAttendeeDTO | null>(null);
	const [busy, setBusy] = useState<boolean>(false);

	const cargar = () => http.workshopsService.details(campusId, id)
		.then(r => { setT(r.data as WorkshopMultiDetailDTO); setPrecio(String(r.data.price ?? "")); setCobra((r.data.price || 0) > 0); })
		.catch(e => notify(errorText(e), false));
	useEffect(() => { cargar();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, id]);

	const vigentes = useMemo(() => (t?.attendees || []).filter(a => a.status === 1), [t]);
	if (!t) return <div className="wau-caja wau-sec">{S.cargando}</div>;
	const soloLectura = t.isHistoric || t.status === "anulado";
	const conSesiones = (t.sessions || []).length > 0;

	const agregar = (s: { id: number; name: string }) => {
		setBusy(true);
		http.workshopsService.addAttendee(campusId, { workshopId: t.id, studentId: s.id, charge: cobra, price: cobra && precio !== "" ? Number(precio) : undefined })
			.then(r => { setT(r.data as WorkshopMultiDetailDTO); notify(S.agregada(s.name)); })
			.catch(e => notify(errorText(e), false))
			.finally(() => setBusy(false));
	};
	const asistio = (a: WorkshopAttendeeDTO, v: boolean) => {
		setT(x => x ? { ...x, attendees: x.attendees.map(y => y.id === a.id ? { ...y, attended: v } : y) } : x);
		http.workshopsService.setAttendance(campusId, a.id, v).catch(e => { notify(errorText(e), false); cargar(); });
	};
	const cobro = (a: WorkshopAttendeeDTO) => {
		if (!a.charge) return <span className="wau-sec">{a.paid ? S.pagado : S.sinCobro}</span>;
		return <><div className="wau-num">{dinero(t.currency, a.charge.total)}</div>
			<div className={a.paid ? "wau-sec" : "wau-oro"} style={{ fontSize: 12.5 }}>{a.paid ? S.pagado : S.porPagar + " " + dinero(t.currency, a.charge.pending)}</div></>;
	};

	return (
		<div>
			<div className="wau-acciones" style={{ justifyContent: "space-between", marginTop: 0, marginBottom: 10 }}>
				<button type="button" className="wau-btn s" onClick={onBack}>‹ {S.volver}</button>
				{t.isHistoric ? <span className="wau-sec">{S.historicoAviso}</span> : <button type="button" className="wau-btn s" onClick={() => setEditar(true)}>{S.editar}</button>}
			</div>
			<div className="wau-caja">
				<div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
					<h3 style={{ margin: 0, fontSize: 20 }}>{t.name}</h3><EstadoPill status={t.status} label={t.statusLabel} />
				</div>
				<div className="wte-datos">
					<div><span className="wau-lab">{S.inicio}</span><span className="wau-num">{fechaHoraAmPm(t.startsAt)}</span></div>
					{t.endsAt ? <div><span className="wau-lab">{S.fin}</span><span className="wau-num">{fechaHoraAmPm(t.endsAt)}</span></div> : null}
					<div><span className="wau-lab">{S.curso}</span>{t.courseName || t.area || "—"}</div>
					<div><span className="wau-lab">{S.aula}</span>{t.classroomName || "—"}</div>
					<div><span className="wau-lab">{S.maestra}</span>{t.mentorName || "—"}{t.mentorFee ? <span className="wau-sec">, {dinero(t.currency, t.mentorFee)}</span> : null}</div>
					<div><span className="wau-lab">{S.precio}</span><span className="wau-num">{dinero(t.currency, t.price)}</span></div>
					<div><span className="wau-lab">{S.inscritas}</span><span className={"wau-num" + (t.spotsLeft === 0 ? " wau-oro" : "")}>{vigentes.length}{t.capacity ? " / " + t.capacity : ""}</span></div>
				</div>
				{t.notes ? <p className="wau-sec" style={{ margin: "10px 0 0 0", fontSize: 13.5 }}>{t.notes}</p> : null}
			</div>

			{t.isHistoric && !conSesiones ? null : <Sesiones campusId={campusId} taller={t} opciones={opciones} soloLectura={soloLectura} onChange={setT} notify={notify} />}

			<div className="wau-caja">
				<h3>{S.inscritas} <span className="wau-sec wau-num">({vigentes.length})</span></h3>
				{soloLectura ? null : (
					<div style={{ marginBottom: 12 }}>
						<div className="wau-fila" style={{ marginBottom: 8 }}>
							<label className="wau-campo" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
								<input type="checkbox" checked={cobra} onChange={e => setCobra(e.target.checked)} /><span>{S.seCobra}</span></label>
							{cobra ? <input type="number" min={0} step="0.01" className="wau-entrada wau-num" style={{ width: 130 }} aria-label={S.precio} value={precio} onChange={e => setPrecio(e.target.value)} /> : null}
						</div>
						<BuscarAlumna campusId={campusId} onPick={agregar} disabled={busy || t.spotsLeft === 0} placeholder={S.buscarAlumna} boton={S.buscarBtn} />
					</div>
				)}
				<div className="wau-tabla">
					<table>
						<thead><tr><th>{S.colAlumna}</th><th>{S.colTel}</th><th>{S.colEstado}</th><th className="der">{S.colCobro}</th><th>{conSesiones ? S.colSesiones : S.colAsistio}</th><th>{S.colNota}</th><th></th></tr></thead>
						<tbody>
							{t.attendees.length === 0 ? <tr><td colSpan={7} className="wau-sec" style={{ textAlign: "center", padding: 18 }}>{S.sinInscritas}</td></tr> : t.attendees.map(a => (
								<tr key={a.id} style={a.status !== 1 ? { opacity: .6 } : undefined}>
									<td><b>{a.studentName}</b><div className="wau-sec" style={{ fontSize: 12.5 }}>{a.identityCode}</div></td>
									<td className="wau-num">{a.phone || "—"}</td>
									<td><span className={"wau-pastilla" + (a.status === 1 ? " acento" : "")}>{a.statusLabel}</span></td>
									<td className="der">{cobro(a)}</td>
									<td>{conSesiones
										? <ChipsSesiones campusId={campusId} taller={t} attendeeId={a.id} editable={!soloLectura && a.status === 1} onChange={setT} notify={notify} />
										: <input type="checkbox" checked={a.attended} disabled={soloLectura || a.status !== 1} aria-label={S.colAsistio} onChange={e => asistio(a, e.target.checked)} />}</td>
									<td style={{ fontSize: 13, maxWidth: 220 }}>{a.note || ""}</td>
									<td style={{ textAlign: "right" }}>{!soloLectura && a.status === 1 ? <button type="button" className="wau-btn s mini" onClick={() => setBaja(a)}>{S.baja}</button> : null}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>

			{soloLectura ? null : <Invitacion campusId={campusId} taller={t} sesiones={t.sessions} opciones={opciones} notify={notify} />}

			{editar ? <WorkshopForm campusId={campusId} opciones={opciones} taller={t} onClose={() => setEditar(false)}
				onSaved={(_x, texto) => { setEditar(false); notify(texto); cargar(); }} /> : null}
			{baja ? <PromptModal title={S.bajaTitulo(baja.studentName)} label={S.bajaNota} confirm={S.baja} optional busy={busy} onCancel={() => setBaja(null)}
				onConfirm={(nota) => {
					setBusy(true);
					http.workshopsService.removeAttendee(campusId, baja.id, nota || undefined)
						.then(r => { setT(r.data as WorkshopMultiDetailDTO); setBaja(null); notify(S.bajaOk); })
						.catch(e => notify(errorText(e), false))
						.finally(() => setBusy(false));
				}} /> : null}
		</div>
	);
};

export default WorkshopDetail;
